import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Email,
  Lock,
  Send,
  Key as OTPKey,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { AutoCloseAlert } from '../../Common';
import useRouteMeta from '../../../hooks/useRouteMeta';

const ResetPassword = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();
  
  const navigate = useNavigate();
  const location = useLocation();

  // Extract email and OTP from URL query parameters with proper decoding
  const queryParams = new URLSearchParams(location.search);
  const rawEmail = queryParams.get('email') || '';
  const initialEmail = decodeURIComponent(rawEmail);
  const initialOtp = queryParams.get('otp') || '';
  

  const [formData, setFormData] = useState({
    email: initialEmail,
    otp: initialOtp,
    new_password: '',
    confirm_new_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    otp: '',
    new_password: '',
    confirm_new_password: '',
  });

  useEffect(() => {
    const isMobile = window.innerWidth < 600; // xs breakpoint
    
    if (isMobile) {
      document.body.style.backgroundColor = 'white';
    } else {
      document.body.style.backgroundColor = '#f5f5f5'; // or whatever your default background color is
    }

    // Cleanup function to restore original background when component unmounts
    return () => {
      document.body.style.backgroundColor = '#f5f5f5'; // restore default
    };
  }, []);

  // Client-side validation functions
  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateOTP = (otp) => {
    if (!otp) return 'OTP is required';
    if (otp.length < 4) return 'OTP must be at least 4 characters';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters long';
    return '';
  };

  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return '';
  };

  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'email':
        return validateEmail(value);
      case 'otp':
        return validateOTP(value);
      case 'new_password':
        return validatePassword(value);
      case 'confirm_new_password':
        return validateConfirmPassword(formData.new_password, value);
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
    setSuccess('');
    
    // Clear field errors when user types
    setFieldErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Client-side validation
    const emailError = validateEmail(formData.email);
    const otpError = validateOTP(formData.otp);
    const passwordError = validatePassword(formData.new_password);
    const confirmPasswordError = validateConfirmPassword(formData.new_password, formData.confirm_new_password);
    
    if (emailError || otpError || passwordError || confirmPasswordError) {
      setFieldErrors({
        email: emailError,
        otp: otpError,
        new_password: passwordError,
        confirm_new_password: confirmPasswordError,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await merchantAuthAPI.resetPassword({
        email: formData.email,
        otp: formData.otp,
        new_password: formData.new_password,
      });
      setSuccess(response.data.message || 'Password has been reset successfully!');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      // Show all server errors as toast (general error)
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            width: '100%',
            boxShadow: { xs: 'none', sm: 3 }, // No shadow for mobile, shadow for larger screens
            border: { xs: 'none', sm: '1px solid #E0E0E0' }, // No border on mobile, border on larger screens
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
            Reset Password
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          <AutoCloseAlert
            severity="success"
            message={success}
            show={!!success}
            onClose={() => setSuccess('')}
            autoCloseDelay={4000}
          />

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6318C6',
                  },
                  backgroundColor: 'white',
                  '&.Mui-filled': {
                    backgroundColor: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: 'inherit',
                  },
                  '& input:-internal-autofill-selected': {
                    backgroundColor: 'white !important',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6318C6',
                },
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px',
                    sm: '16px'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#6318C6' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="otp"
              label="One-Time Password (OTP)"
              name="otp"
              value={formData.otp}
              onChange={handleChange}
              disabled
              error={!!fieldErrors.otp}
              helperText={fieldErrors.otp}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6318C6',
                  },
                  backgroundColor: 'white',
                  '&.Mui-filled': {
                    backgroundColor: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: 'inherit',
                  },
                  '& input:-internal-autofill-selected': {
                    backgroundColor: 'white !important',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6318C6',
                },
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px',
                    sm: '16px'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <OTPKey sx={{ color: '#6318C6' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="new_password"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              id="new_password"
              autoComplete="new-password"
              value={formData.new_password}
              onChange={handleChange}
              disabled={loading}
              error={!!fieldErrors.new_password}
              helperText={fieldErrors.new_password}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6318C6',
                  },
                  backgroundColor: 'white',
                  '&.Mui-filled': {
                    backgroundColor: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: 'inherit',
                  },
                  '& input:-internal-autofill-selected': {
                    backgroundColor: 'white !important',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6318C6',
                },
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px',
                    sm: '16px'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#6318C6' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff sx={{ color: '#6318C6' }} /> : <Visibility sx={{ color: '#6318C6' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirm_new_password"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirm_new_password"
              autoComplete="new-password"
              value={formData.confirm_new_password}
              onChange={handleChange}
              disabled={loading}
              error={!!fieldErrors.confirm_new_password}
              helperText={fieldErrors.confirm_new_password}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6318C6',
                  },
                  backgroundColor: 'white',
                  '&.Mui-filled': {
                    backgroundColor: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: 'inherit',
                  },
                  '& input:-internal-autofill-selected': {
                    backgroundColor: 'white !important',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6318C6',
                },
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px',
                    sm: '16px'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#6318C6' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff sx={{ color: '#6318C6' }} /> : <Visibility sx={{ color: '#6318C6' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                background: '#6318C6',
                '&:hover': {
                  background: '#5a4db2',
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <Send sx={{ mr: 1 }} /> Reset Password
                </>
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link 
                to="/login" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#6318C6',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword; 